import type { EmployerJob } from './employerJobs';
import type { AdminModerationJob } from './adminModeration';
import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');
const LOCAL_STORAGE_KEY = 'lighthouse_job_draft';

export type JobDraftPayload = {
  title?: string;
  location?: string;
  type?: string;
  salary?: string;
  summary?: string;
  responsibilities?: string;
  requirements?: string;
  occupationId?: string;
  occupationTitle?: string;
};

export type JobDraft = JobDraftPayload & {
  id: string;
  updatedAt: string;
};

const randomId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `draft-${Date.now()}`;
};

const saveLocalDraft = (draft: JobDraft) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(draft));
};

const readLocalDraft = (): JobDraft | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as JobDraft;
  } catch {
    return null;
  }
};

const persistDraft = (draft: JobDraft) => {
  saveLocalDraft(draft);
  return draft;
};

const clearLocalDraft = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(LOCAL_STORAGE_KEY);
};

export const getStoredJobDraft = (): JobDraft | null => readLocalDraft();
export const clearStoredJobDraft = () => clearLocalDraft();

export async function createJobDraft(payload: JobDraftPayload): Promise<JobDraft> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to create job drafts.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/jobs/drafts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Draft create failed (${response.status}): ${errorText}`);
  }
  const draft = (await response.json()) as JobDraft;
  return persistDraft(draft);
}

export async function updateJobDraft(id: string, payload: JobDraftPayload): Promise<JobDraft> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update job drafts.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/jobs/drafts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Draft update failed (${response.status}): ${errorText}`);
  }
  const draft = (await response.json()) as JobDraft;
  return persistDraft(draft);
}

export async function fetchJobDraft(id: string): Promise<JobDraft | undefined> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to fetch job drafts.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/jobs/drafts/${id}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (response.status === 404) {
    return undefined;
  }
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Draft fetch failed (${response.status}): ${errorText}`);
  }
  const draft = (await response.json()) as JobDraft;
  return persistDraft(draft);
}

export type JobSubmissionPayload = JobDraftPayload & {
  employerId: string;
  employerName?: string;
};

export type JobSubmissionResult = {
  job: EmployerJob;
  moderation: AdminModerationJob;
};

const buildDescription = (payload: JobDraftPayload) => {
  const blocks = [
    payload.summary ? `Summary:\n${payload.summary}` : null,
    payload.responsibilities ? `Responsibilities:\n${payload.responsibilities}` : null,
    payload.requirements ? `Requirements:\n${payload.requirements}` : null,
  ].filter(Boolean);
  return blocks.join('\n\n') || 'Awaiting inclusive review.';
};
export async function submitJobDraft(draftId: string, payload: JobSubmissionPayload): Promise<JobSubmissionResult> {
  if (!draftId) {
    throw new Error('Draft ID is required before submitting for moderation.');
  }

  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to submit job drafts.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/jobs/drafts/${draftId}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Draft submission failed (${response.status}): ${errorText}`);
  }

  const result = (await response.json()) as JobSubmissionResult;
  clearLocalDraft();
  return result;
}
